import { makeAutoObservable } from "mobx";
import type { CHVerificationFormValues } from "helpers/forms/incorporation/chVerificationTypes";
import type { AnalyticsManager } from "services/analytics/analyticsManager";
import type { CompanyStore } from "services/company/companyStore";
import type { ConsentStore } from "services/consent/consentStore";
import { ConsentType } from "services/consent/consentTypes";
import type { DirectorStore } from "services/director/directorStore";
import type { FeatureStore } from "services/feature/featureStore";
import type { IncorporationStore } from "services/incorporation/incorporationStore";
import { IncorporationStatus } from "services/incorporation/incorporationTypes";

export enum CHVerificationEntity {
  CHMultiDirectorVerificationCompleted = "CHMultiDirectorVerificationCompleted",
  CHHasPersonalCode = "CHHasPersonalCode",
  CHIdvConsent = "CHIdvConsent",
  CHRequestIdConfirmation = "CHRequestIdConfirmation",
  CHEnterPersonalCode = "CHEnterPersonalCode",
  CHRejectionFlowSubmission = "CHRejectionFlowSubmission",
}

export class CHVerificationStepStore {
  constructor(
    private readonly _directorStore: DirectorStore,
    private readonly _companyStore: CompanyStore,
    private readonly _incorporationStore: IncorporationStore,
    private readonly _analyticsManager: AnalyticsManager,
    private readonly _featureStore: FeatureStore,
    private readonly _consentStore: ConsentStore,
  ) {
    makeAutoObservable(this);
  }

  private get _isAcspIdvSubmissionEnabled(): boolean {
    return this._featureStore.hasFeature("formationAcspIdvSubmissionEnabled");
  }

  // When the compliance call is enabled, the happy-path submission is deferred past CH verification:
  // the call comes first, then the compliance-wait step calls createIncorporation once that check
  // succeeds.
  private get _isComplianceCallEnabled(): boolean {
    return this._featureStore.hasFeature("formationComplianceCall");
  }

  async updateEntity(data: CHVerificationFormValues, entity: CHVerificationEntity): Promise<void> {
    const { directors } = this._directorStore;
    const { incorporation } = this._incorporationStore;
    const isRejectedFlow = incorporation?.status === IncorporationStatus.Rejected;

    switch (entity) {
      case CHVerificationEntity.CHMultiDirectorVerificationCompleted:
        if (directors.length > 1 && !isRejectedFlow && !this._isComplianceCallEnabled) {
          await this._incorporationStore.createIncorporation();
          this._analyticsManager.event("ch_verification_incorporation_created_after_secondary_director");
        }

        if (isRejectedFlow) {
          this._analyticsManager.event("ch_verification_multi_director_rejected_resolve_completed");
        }
        await this._companyStore.init();
        this._analyticsManager.event("ch_verification_secondary_director_completed");
        break;
      case CHVerificationEntity.CHHasPersonalCode:
        this._analyticsManager.event("ch_verification_user_answered_has_code", {
          hasCode: data.hasPersonalCode,
        });
        break;
      case CHVerificationEntity.CHIdvConsent:
        if (!data.acspConsent || this._consentStore.consentsData.includes(ConsentType.AcspIdentityVerification)) {
          return;
        }
        await this._consentStore.acceptAcspIdentityVerificationConsent();
        this._analyticsManager.event("ch_verification_idv_consent_confirmed");
        break;
      case CHVerificationEntity.CHRequestIdConfirmation:
        if (this._isAcspIdvSubmissionEnabled) {
          await this._directorStore.submitMainDirectorACSPVerification();
          this._analyticsManager.event("ch_verification_id_confirmation_requested");
        } else {
          this._analyticsManager.event("ch_verification_id_confirmation_self_service");
        }
        break;
      case CHVerificationEntity.CHEnterPersonalCode: {
        await this._directorStore.updateCHPersonalCode(this._directorStore.getMainDirector().id, data.chPersonalCode);
        this._analyticsManager.event("ch_verification_personal_code_entered");

        if (directors.length === 1 && !isRejectedFlow && !this._isComplianceCallEnabled) {
          await this._incorporationStore.createIncorporation();
          this._analyticsManager.event("ch_verification_incorporation_created");
        }
        if (isRejectedFlow) {
          this._analyticsManager.event("ch_verification_single_director_rejected_resolve_completed");
        }
        await this._directorStore.init();
        break;
      }
      case CHVerificationEntity.CHRejectionFlowSubmission:
        if (isRejectedFlow) {
          // submit incorporation
          await this._incorporationStore.createIncorporation();
          this._analyticsManager.event("ch_verification_single_director_rejected_flow_submission");
        }
        break;
    }
  }
}
